import {FC, ReactNode, useCallback, useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {Alert, Button, Card, Col, Form, Input, message, Modal, notification, Row, Select, Space, Tag, Tooltip, Typography} from 'antd';
import {EditOutlined} from '@ant-design/icons';
import {Image, HighlighterCircle, ArrowFatLineUp, EnvelopeSimple, UserFocus} from "@phosphor-icons/react";
import {useMutation, useQuery, useQueryClient, UseQueryResult} from "@tanstack/react-query";
import {
    getHomeofficeOptions,
    getJobById,
    getJobTypes,
    getJobSubscriptions,
    getStripeUrl,
    getTargetGroupOptions,
    updateJobDemand
} from "../../services/api";
import {ENGAGEMENT_FORM_OPTIONS, WORK_LOAD_HOURS_OPTIONS} from "../../util/jobFormOptions.ts";
import {GeocodedMap} from "../../components/Map";
import {IJobDetails, IJobSubscription, ILocation, JobTypeOption} from "../../services/types.ts";
import {CancelSubscriptionModal} from "../jobSubscriptions/CancelSubscriptionModal.tsx";
import {AddressModal} from "../../components/AddressModal.tsx";
import {AuthContext} from "../../context/AuthContext.tsx";
import {PageTitle} from "candidate/src/components/PageTitle.tsx";
import {Loader} from "../../components/Loader.tsx";
import {useMediaQuery} from "@uidotdev/usehooks";
import {WhereWrapper} from "candidate/src/pages/jobDetails/JobDetails.tsx";
import {getToken} from "../../util/tokenCookies.ts";
import ReactQuill from "react-quill";

const {Title, Text} = Typography;

const ADDON_CONFIG: { key: keyof IJobDetails; label: string; icon: ReactNode; color: string }[] = [
    {key: 'has_logo', label: 'Logo', icon: <Image size={14} weight="bold"/>, color: 'blue'},
    {key: 'has_highlight', label: 'Hervorgehoben', icon: <HighlighterCircle size={14} weight="bold"/>, color: 'orange'},
    {key: 'has_top_position', label: 'Top-Anzeige', icon: <ArrowFatLineUp size={14} weight="bold"/>, color: 'gold'},
    {key: 'has_email_distribution', label: 'E-Mail-Verteilung', icon: <EnvelopeSimple size={14} weight="bold"/>, color: 'purple'},
    {key: 'has_direct_contact', label: 'Direktansprache', icon: <UserFocus size={14} weight="bold"/>, color: 'cyan'},
];

const CATEGORY_LABELS: Record<string, string> = {
    fachkraefte: 'Fachkräfte',
    hilfskraefte: 'Hilfskräfte',
};

const modalTitles: { [key: string]: string } = {
    title: 'Stellentitel',
    description: 'Beschreibung der offenen Stelle',
    target_group_label: 'Zielgruppe der Stellenanzeige',
    homeoffice_label: 'Homeoffice Möglichkeiten',
    job_type_label: 'Beschäftigungsart',
    work_load_label: 'Arbeitspensum',
}

export const JobDetails: FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const {user} = useContext(AuthContext);
    const {id = ""} = useParams<{ id: string }>();
    const [form] = Form.useForm();
    const [editField, setEditField] = useState<keyof IJobDetails | 'job_type_and_work_load' | null>(null);
    const [editValue, setEditValue] = useState<string | number | undefined>("");
    const [editWorkTypeAndLoad, setEditWorkTypeAndLoad] = useState<{
        job_type_label?: string | number;
        engagement_form?: string | null;
        work_load_min_hours?: number | null;
        work_load_max_hours?: number | null;
    } | null>(null);
    const queryClient = useQueryClient()

    const [isLocationEditorOpen, setIsLocationEditorOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState<ILocation | null>(null);
    const [isPublishModalVisible, setIsPublishModalVisible] = useState(false);
    const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
    const isMobile = useMediaQuery("only screen and (max-width : 768px)");

    const isPaymentSuccess = new URLSearchParams(location.search).get('payment') === 'success';

    const {data, isLoading, error, refetch}: UseQueryResult<IJobDetails, Error> = useQuery({
        queryKey: ['jobs', id],
        queryFn: () => getJobById(id),
        enabled: !!id,
        gcTime: 0,
    });

    const {data: jobTypes}: UseQueryResult<JobTypeOption[], Error> = useQuery({
        queryKey: ['jobTypes'],
        queryFn: getJobTypes,
    });

    const {data: homeofficeOptions}: UseQueryResult<JobTypeOption[], Error> = useQuery({
        queryKey: ['homeofficeOptions'],
        queryFn: getHomeofficeOptions,
    });

    const {data: targetGroupOptions}: UseQueryResult<JobTypeOption[], Error> = useQuery({
        queryKey: ['targetGroupOptions'],
        queryFn: getTargetGroupOptions,
    });

    const isPerJobPricing = data?.pricing_model === 'per_job';

    const {data: jobSubscriptions, isLoading: isLoadingSubscriptions} = useQuery({
        queryKey: ['jobSubscriptions'],
        queryFn: getJobSubscriptions,
        enabled: isPerJobPricing,
    });

    const currentSubscription: IJobSubscription | undefined = jobSubscriptions?.find(
        (sub) => Number(sub.job_demand_id) === Number(data?.id) && (
            sub.status === 'active' || sub.status === 'expiring_soon' ||
            (sub.status === 'canceled' && sub.expiration_date && sub.expiration_date > Date.now())
        )
    );

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.get('payment') === 'success') {
            setIsPublishModalVisible(true);
            // Clean up the URL to remove the parameter
            const newUrl = `${location.pathname}${location.search.replace('?payment=success', '')}`;
            window.history.replaceState({}, '', newUrl);
        }
    }, [location]);

    const updateMutation = useMutation({
        mutationFn: (updatedData: Partial<IJobDetails>) => updateJobDemand(id, updatedData),
        onSuccess: () => {
            message.success('Eingabe erfolgreich gespeichert');
            setIsPublishModalVisible(prevState => {
                if (prevState) {
                    navigate('/jobs')
                }
                return false
            })
            setEditField(null);
            setEditWorkTypeAndLoad(null);
            queryClient.invalidateQueries({queryKey: ['jobs', id]})
            queryClient.invalidateQueries({queryKey: ['user']})
            refetch();
        },
        onError: (error) => {
            message.error(`Die Stellenausschreibung konnte nicht aktualisiert werden: ${error}`);
        },
    });

    const handleEdit = (fieldToEdit: keyof IJobDetails | 'job_type_and_work_load') => {
        setEditField(fieldToEdit);
    };

    useEffect(() => {
        if (!editField || !data) {
            if (editField === null) {
                form.resetFields();
                setEditValue("");
                setEditWorkTypeAndLoad(null);
            }
            return;
        }

        form.resetFields();

        const normalizeString = (str: string | undefined | null): string => {
            if (!str) return "";
            return str.replace(/\s+/g, ' ').trim();
        }

        if (editField === 'title') {
            setEditValue(data.title || "");
            setEditWorkTypeAndLoad(null);
            form.setFieldsValue({ title: data.title || "" });
        } else if (editField === 'description') {
            setEditValue(data.description || "");
            setEditWorkTypeAndLoad(null);
            form.setFieldsValue({ description: data.description || "" });
        } else if (editField === 'homeoffice_label') {
            const normalizedDataLabel = normalizeString(data.homeoffice_label);
            const selectedOption = homeofficeOptions?.find(opt => normalizeString(opt.label) === normalizedDataLabel);
            setEditValue(selectedOption?.id);
            setEditWorkTypeAndLoad(null);
            form.setFieldsValue({ homeoffice_label: selectedOption?.id });
        } else if (editField === 'target_group_label') {
            const normalizedDataLabel = normalizeString(data.target_group_label);
            const selectedOption = targetGroupOptions?.find(opt => normalizeString(opt.label) === normalizedDataLabel);
            setEditValue(selectedOption?.id);
            setEditWorkTypeAndLoad(null);
            form.setFieldsValue({ target_group_label: selectedOption?.id });
        } else if (editField === 'job_type_and_work_load') {
            const normalizedJobTypeLabel = normalizeString(data.job_type_label);
            const foundJobTypeOption = jobTypes?.find(opt => normalizeString(opt.label) === normalizedJobTypeLabel);

            const newWorkTypeAndLoadState = {
                job_type_label: foundJobTypeOption?.id,
                engagement_form: data.engagement_form ?? undefined,
                work_load_min_hours: data.work_load_min_hours ?? undefined,
                work_load_max_hours: data.work_load_max_hours ?? undefined,
            };
            setEditWorkTypeAndLoad(newWorkTypeAndLoadState);
            form.setFieldsValue(newWorkTypeAndLoadState);
            setEditValue(undefined);
        }
    }, [editField, data, jobTypes, homeofficeOptions, targetGroupOptions, form]);

    const handleSave = useCallback(() => {
        if (!data) return;

        let updatedData: Partial<IJobDetails> = { ...data };

        if (editField === 'job_type_and_work_load' && editWorkTypeAndLoad) {
            const formValues = form.getFieldsValue();
            const jobTypeId = formValues.job_type_label ?? editWorkTypeAndLoad.job_type_label;
            const min = formValues.work_load_min_hours ?? editWorkTypeAndLoad.work_load_min_hours;
            const max = formValues.work_load_max_hours ?? editWorkTypeAndLoad.work_load_max_hours;
            updatedData.job_type_id = jobTypeId as number | undefined;
            updatedData.job_type_label = jobTypes?.find(opt => opt.id === jobTypeId)?.label;
            updatedData.engagement_form = formValues.engagement_form ?? editWorkTypeAndLoad.engagement_form ?? null;
            updatedData.work_load_min_hours = min ?? null;
            updatedData.work_load_max_hours = max ?? null;
            if (min != null && max != null) {
                updatedData.work_load_id = 0;
                updatedData.work_load_label = `von ${min} bis ${max} Stunden pro Woche`;
            } else {
                updatedData.work_load_label = data.work_load_label;
            }
        } else if (editField === 'homeoffice_label') {
            updatedData.homeoffice_id = editValue as number | undefined;
            updatedData.homeoffice_label = homeofficeOptions?.find(opt => opt.id === editValue)?.label;
        } else if (editField === 'target_group_label') {
            updatedData.target_group_id = editValue as number | undefined;
            updatedData.target_group_label = targetGroupOptions?.find(opt => opt.id === editValue)?.label;
        } else if (editField === 'title' || editField === 'description') {
            if (editField in data) {
                updatedData = { ...updatedData, [editField]: editValue };
            }
        } else {
            if (editField && editField in data) {
                updatedData = { ...updatedData, [editField]: editValue };
            }
        }

        if (editField) {
            updateMutation.mutate(updatedData);
        }
    }, [editField, editValue, data, updateMutation, editWorkTypeAndLoad, jobTypes, homeofficeOptions, targetGroupOptions, form]);

    const handleEditLocation = () => {
        setIsLocationEditorOpen(true);
    };

    const handleSaveLocation = useCallback((location: ILocation | null) => {
        if (location && data) {
            const updatedData = {
                ...data,
                search_location_label: location.label_client,
                search_location_id: location.id,
                regions_id: location.type === 'region_id' ? location.value || 0 : 0,
                postal_code: location.type !== 'region_id' ? location.value?.toString() || '' : '',
                radius: location.radius || 0,
            };
            updateMutation.mutate(updatedData);
            setSelectedLocation(location);
        }
        setIsLocationEditorOpen(false);
    }, [data, updateMutation]);

    const isJobActive = isPerJobPricing
        ? data?.job_demand_status_id === 4 || data?.status_label?.toLowerCase().trim() === 'aktiv'
        : data?.job_demand_status_id === 4

    const actionButtonHandler = async () => {
        if (!isJobActive) {
            if (isPerJobPricing && data?.id) {
                navigate(`/new/job?draftId=${data.id}`);
                return;
            }
            if ((user?.plan_type === "paid" && !user?._client?.isPrivate && ((user?.jobs_current_month ?? 0) < (user?.jobs_allowed_monthly ?? 0))) || (user?.jobs_current_month || 0) < (user?.privateJobsAllowed || 0)) {
                setIsPublishModalVisible(true);
            } else {
                if (
                    data?.id && user?._client?.isPrivate && (user?.jobs_current_month ?? 0) < (user?.privateJobsAllowed ?? 0)
                ) {
                    const authToken = getToken();
                    const stripeResponse = await getStripeUrl({
                        id: authToken,
                        product: "Private",
                        redirectPath: `jobs/${data.id}`
                    });
                    if (stripeResponse.url) {
                        window.location.href = stripeResponse.url;
                    } else {
                        notification.error({message: "Es ist ein Fehler beim Anlegen der Stellenanzeige aufgetreten."});
                    }
                } else {
                    navigate("/finances")
                }
            }
        } else if (isPerJobPricing) {
            if (isLoadingSubscriptions) return;
            if (currentSubscription && currentSubscription.status !== 'canceled') {
                // Per-job pricing with active subscription: open cancel modal
                setIsCancelModalVisible(true);
            } else {
                // Per-job pricing without subscription: deactivate directly
                const updatedData = {
                    ...data,
                    job_demand_status_id: 3
                };
                updateMutation.mutate(updatedData);
            }
        } else {
            // Legacy: deactivate directly
            const updatedData = {
                ...data,
                job_demand_status_id: 3
            };
            updateMutation.mutate(updatedData);
        }
    }

    const handlePublishJob = () => {
        if (data) {
            const updatedData = {
                ...data,
                job_demand_status_id: 4
            };
            updateMutation.mutate(updatedData);
        }
    };


    if (isLoading) {
        return <Loader/>
    }

    if (error) return <Alert message="Error" description={error.message} type="error"/>;
    if (!data) return <Alert message="No job data found" type="warning"/>;

    const address = data.search_location_id === -2 ? "Berlin Germany" : data.postal_code ? data.search_location_label : data.city + " Germany";

    const displayAddress = data.search_location_id === -2 ? "Homeoffice" : data.search_location_label;
    const hideMainButton = !isPerJobPricing && data?.job_demand_status_id !== 4 && data?.type === 'job_ad';

    const mainBtnText = isJobActive
        ? "Deaktivieren"
        : isPerJobPricing
            ? "Aktivieren"
            : (user?.plan_type === "paid" && !user?._client?.isPrivate && ((user?.jobs_current_month ?? 0) < (user?.jobs_allowed_monthly ?? 0))) || ((user?.jobs_current_month ?? 0) < (user?.privateJobsAllowed ?? 0))
                ? "Aktivieren"
                : user?._client?.isPrivate && ((user?.jobs_current_month ?? 0) >= (user?.privateJobsAllowed ?? 0))
                    ? "Weiter zur Zahlung"
                    : "Plan upgraden";

    return (
        <div>
            <PageTitle onMainBtnClick={hideMainButton ? undefined : actionButtonHandler}
                       mainBtnText={hideMainButton ? undefined : mainBtnText}
                       isLoading={updateMutation.isPending}>
                <div>
                    <Title level={4} style={{margin: 0}}>Offene Stelle <EditOutlined
                        onClick={() => handleEdit('title')}/>
                    </Title>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <Title level={2} style={{margin: 0}}>{data.title}</Title>
                        {hideMainButton && <Tag color="red">Inaktiv</Tag>}
                        {isJobActive && currentSubscription?.status === 'canceled' && currentSubscription?.expiration_date && (
                            <Tag color="orange">
                                aktiv bis {new Date(currentSubscription.expiration_date).toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'})}
                            </Tag>
                        )}
                    </div>
                </div>
            </PageTitle>
            <Row gutter={[16, 16]}>
                <Col span={isMobile ? 24 : 12}>
                    <Card style={{marginBottom: '24px'}}>
                        {data.job_category && (
                            <Row style={{padding: '8px 0'}}>
                                <Col>
                                    <Text strong>Anzeigenart</Text>
                                    <br/>
                                    <Tag color={data.job_category === 'fachkraefte' ? 'blue' : 'green'} style={{marginTop: 4}}>
                                        {CATEGORY_LABELS[data.job_category] ?? data.job_category}
                                    </Tag>
                                </Col>
                            </Row>
                        )}
                        {ADDON_CONFIG.some(a => data[a.key]) && (
                            <Row style={{padding: '8px 0'}}>
                                <Col>
                                    <Text strong>Aktive Zusatzoptionen</Text>
                                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4}}>
                                        {ADDON_CONFIG.filter(a => data[a.key]).map(addon => (
                                            <Tooltip key={addon.key} title={addon.label}>
                                                <Tag color={addon.color} style={{display: 'flex', alignItems: 'center', gap: 4}}>
                                                    {addon.icon}
                                                    {addon.label}
                                                </Tag>
                                            </Tooltip>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        )}
                        <DetailItem title="Zielgruppe" content={data.target_group_label}
                                    onEdit={() => handleEdit('target_group_label')}/>
                        <DetailItem title="Homeoffice - Möglichkeiten" content={data.homeoffice_label}
                                    onEdit={() => handleEdit('homeoffice_label')}/>
                        <DetailItem
                            title="Beschäftigungsart, Einsatzform und Arbeitspensum"
                            content={<>
                                Beschäftigungsart: {data.job_type_label} <br/>
                                Einsatzform: {data.engagement_form ?? '—'} <br/>
                                Arbeitspensum: {data.work_load_min_hours != null && data.work_load_max_hours != null
                                    ? `von ${data.work_load_min_hours} bis ${data.work_load_max_hours} Stunden pro Woche`
                                    : data.work_load_label}
                            </>
                            }
                            onEdit={() => handleEdit('job_type_and_work_load')}
                        />
                    </Card>
                    <Card>
                        <Title level={5}>Stellenbeschreibung<EditOutlined
                            onClick={() => handleEdit('description')}/>
                        </Title>
                        <Text style={{
                            textAlign: "justify",
                            display: "block"
                        }}><WhereWrapper>
                            <div dangerouslySetInnerHTML={{__html: data.description}}/>
                        </WhereWrapper></Text>
                    </Card>
                </Col>
                <Col span={isMobile ? 24 : 12}>
                    <Card title="Standort" extra={<EditOutlined onClick={handleEditLocation}/>}>
                        <Text>Wo?: {selectedLocation?.label || displayAddress}</Text>
                        <Alert
                            message={!selectedLocation?.label_client?.includes("Region:") ?
                                "Legen Sie für jeden Standort eine eigene Stellenanzeige an um alle Kandidaten zu erreichen" :
                                "Wählen Sie soweit möglich immer eine Postleitzahl und legen Sie für jeden Standort eine eigene Stellenanzeige an"
                            }
                            type="info"
                            showIcon
                            style={{marginTop: 8, marginBottom: 8}}
                        />
                        <GeocodedMap address={address}/>
                    </Card>
                </Col>
            </Row>

            <Modal
                key={editField || 'modal-closed'}
                title={editField === 'job_type_and_work_load' ? 'Beschäftigungsart, Einsatzform und Arbeitspensum' : (editField ? modalTitles[editField] : '')}
                open={!!editField}
                onOk={handleSave}
                okText="Speichern"
                cancelText="Abbrechen"
                onCancel={() => {
                    setEditField(null);
                }}
            >
                <Form layout="vertical" form={form}>
                    {editField === 'description' && (
                        <Form.Item name="description"
                                   rules={[{required: true, message: 'Please enter a description'}]}>
                            <ReactQuill theme="snow" placeholder="[Tipp: Beschreiben Sie kurz und prägnant worum es geht] Beispiel: Wir suchen einen neuen Kolleg" value={editValue as string || ''} onChange={setEditValue}/>
                        </Form.Item>
                    )}
                    {editField === 'title' && (
                        <Form.Item name="title"
                                   rules={[{required: true, message: 'Please enter a title'}]}>
                            <Input size={isMobile ? "large" : "middle"} value={editValue as string || ''} onChange={(e) => setEditValue(e.target.value)}/>
                        </Form.Item>
                    )}
                    {(editField === 'job_type_and_work_load') && editWorkTypeAndLoad && (
                        <>
                            <Form.Item name="job_type_label" label="Beschäftigungsart">
                                <Select
                                    key={`job-type-select-${editField}`}
                                    placeholder="Beschäftigungsart auswählen"
                                    options={jobTypes}
                                    value={editWorkTypeAndLoad.job_type_label}
                                    fieldNames={{ label: 'label', value: 'id' }}
                                    onChange={(value) => setEditWorkTypeAndLoad(prev => ({ ...prev, job_type_label: value }))}
                                />
                            </Form.Item>
                                    <Form.Item name="engagement_form" label="Einsatzform">
                                <Select
                                    placeholder="Einsatzform auswählen"
                                    options={ENGAGEMENT_FORM_OPTIONS}
                                    allowClear
                                />
                            </Form.Item>
                            <Form.Item label="Arbeitspensum pro Woche">
                                <Space.Compact style={{width: '100%'}}>
                                    <Form.Item name="work_load_min_hours" noStyle>
                                        <Select placeholder="von (min.)" options={WORK_LOAD_HOURS_OPTIONS} style={{minWidth: 100}} allowClear/>
                                    </Form.Item>
                                    <Form.Item name="work_load_max_hours" noStyle rules={[({getFieldValue}) => ({validator(_, v) { const min = getFieldValue('work_load_min_hours'); if (v != null && min != null && v < min) return Promise.reject(new Error('Max. ≥ Min.')); return Promise.resolve();}})]}>
                                        <Select placeholder="bis (max.)" options={WORK_LOAD_HOURS_OPTIONS} style={{minWidth: 100}} allowClear/>
                                    </Form.Item>
                                </Space.Compact>
                            </Form.Item>
                        </>
                    )}
                    {editField === 'homeoffice_label' && (
                        <Form.Item name="homeoffice_label">
                            <Select
                                placeholder="Homeoffice Möglichkeiten auswählen"
                                options={homeofficeOptions}
                                value={editValue}
                                fieldNames={{ label: 'label', value: 'id' }}
                                onChange={(value) => setEditValue(value)}
                            />
                        </Form.Item>
                    )}
                    {editField === 'target_group_label' && (
                        <Form.Item name="target_group_label">
                            <Select
                                placeholder="Zielgruppe auswählen"
                                options={targetGroupOptions}
                                value={editValue}
                                fieldNames={{ label: 'label', value: 'id' }}
                                allowClear
                                onChange={(value) => setEditValue(value)}
                            />
                        </Form.Item>
                    )}
                </Form>
            </Modal>
            <AddressModal
                open={isLocationEditorOpen}
                onClose={() => setIsLocationEditorOpen(false)}
                onSave={handleSaveLocation}
                initialValue={null}
            />
            <Modal
                title="Bestätigen Sie bitte, dass Sie diese Stellenanzeige veröffentlichen möchten"
                open={isPublishModalVisible}
                onOk={handlePublishJob}
                onCancel={() => setIsPublishModalVisible(false)}
                okText="Veröffentlichen"
                footer={[
                    !isPaymentSuccess &&
                    <Button key="cancel" type="default" onClick={() => setIsPublishModalVisible(false)}>
                        Zurück
                    </Button>,
                    <Button key="ok" type="primary" onClick={handlePublishJob}>
                        Veröffentlichen
                    </Button>
                ]}
            >
                <p>Hinweis: Wenn Sie diese Stellenanzeige zum ersten mal veröffentlichen, werden alle darauf passenden
                    Kandidaten informiert. Stellen Sie daher sicher, dass die Stellenanzeige einen aussagekräftigen
                    Titel und eine gute Stellenbeschreibung hat.</p>
            </Modal>

            {currentSubscription && (
                <CancelSubscriptionModal
                    subscription={currentSubscription}
                    open={isCancelModalVisible}
                    onClose={() => setIsCancelModalVisible(false)}
                    onSuccess={() => {
                        setIsCancelModalVisible(false);
                        queryClient.invalidateQueries({queryKey: ['jobs', id]});
                        queryClient.invalidateQueries({queryKey: ['jobSubscriptions']});
                        queryClient.invalidateQueries({queryKey: ['user']});
                        refetch();
                    }}
                />
            )}
        </div>
    );
};

interface DetailItemProps {
    title: string;
    content: string | ReactNode
    onEdit: () => void;
}

const DetailItem: FC<DetailItemProps> = ({title, content, onEdit}) => (
    <Row justify="space-between" align="middle" style={{padding: '8px 0'}}>
        <Col>
            <Text strong>{title}</Text>
            <br/>
            <Text>{content}</Text>
        </Col>
        <Col>
            <EditOutlined onClick={onEdit}/>
        </Col>
    </Row>
);
